import omit from 'lodash/omit';
import forIn from 'lodash/forIn';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { takeUntil, filter } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { format } from 'date-fns';
import { PHX_DATE_FORMAT } from 'phx-core';

import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import {
  DATE_FORMAT,
  DATE_RANGE_KEY,
  ISort,
  mapArrayFromRouteParams,
  mapBooleanFromRouteParams,
  mapDateRangeFromRouteParams,
  mapSelectedDateRangeFromRouteParams,
  PAGE_NO_KEY,
  PER_PAGE_KEY,
  StateManager,
} from '@app/lib/state-manager';
import { isIgnoredValue } from './utils';
import { ReportsPhxMigrationConfig } from '@services/dynamic-configs/reports-phx-migration';

import {
  ReportParamsType,
  IFilterConfig,
  FILTER_STATE,
  FILTER_DATA_TYPE,
  instanceOfIDateFilterConfig,
  OperatorType,
} from './config-report.interface';
import { REPORT_ID_KEY, REPORT_TYPE_KEY } from '../save-schedule-report/save-and-schedule-reports.constant';

@Injectable()
export class ConfigFiltersService implements OnDestroy {
  public params: ReportParamsType = new Map(); // current params being used
  public queryParamsSubject: BehaviorSubject<Params> = new BehaviorSubject(null); // Provides stream of params in object type
  public queryParamsMapSubject: BehaviorSubject<ReportParamsType> =
    new BehaviorSubject(null); // Provides stream of params in map type
  public filtersConfig: IFilterConfig[];
  public reportKey: string;
  private isDatePickersPhxMigrationEnabled: boolean = false;
  private stateManager: StateManager;
  private unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private reportsPhxMigrationConfig: ReportsPhxMigrationConfig,
  ) {
    this.isDatePickersPhxMigrationEnabled = reportsPhxMigrationConfig.isDatePickerPhxMigrationEnabled();
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public initConfigFilterService(stateManager: StateManager, filters: IFilterConfig[]): void {
    this.filtersConfig = filters;
    this.stateManager = stateManager;
    this.setStatesForFilters();
    this.initParamsStream();
  }

  /**
   * This function navigates to provided params and removes the default values from the url.
   * @param {Object} params the params that we want to navigate to
   * @param {boolean} [onlyShowUserSelectedFilter=true] it omits the filter which user has not change from params,
   *  i.e it doesnt not set default value to url.
   */
  public navigateToRoute(params: Params, onlyShowUserSelectedFilter: boolean = true): void {
    // Hardcoding params which are needed and should not be removed like content_id
    const allParams: Params = this.activatedRoute.snapshot.params;
    const requiredKeys: string[] = ['content_id', 'report_route'];
    requiredKeys
      .filter((key: string) => !!allParams[key])
      .forEach((key: string) => params[key] = allParams[key])
    ;

    if (onlyShowUserSelectedFilter) {
      this.filtersConfig.forEach((filterItem: IFilterConfig) => {
        if (instanceOfIDateFilterConfig(filterItem)) return;
        if (filterItem.type === FILTER_DATA_TYPE.SORT) {
          if (params[filterItem.key.field] === filterItem.defaultValue.field &&
            params[filterItem.key.direction] === filterItem.defaultValue.direction
          ) {
            params = omit(params, filterItem.key.field);
            params = omit(params, filterItem.key.direction);
          }
          return; // Return added so if values are not default then goto next iteration
        }

        if (
          filterItem.type !== FILTER_DATA_TYPE.ENTITY_TYPEAHEAD &&
          filterItem.type !== FILTER_DATA_TYPE.DRIVER_TYPEAHEAD &&
          filterItem.type !== FILTER_DATA_TYPE.VEHICLE_TYPEAHEAD &&
          filterItem.type !== FILTER_DATA_TYPE.ASSET_TYPEAHEAD &&
          (filterItem.state === FILTER_STATE.HIDDEN ||
            filterItem.defaultValue === params[filterItem.key] ||
            isEqual(filterItem.defaultValue, params[filterItem.key]))
        ) {
          params = omit(params, filterItem.key);
        }
      });
    }
    const options = { relativeTo: this.activatedRoute };
    this.router.navigate(['./', params], options);
  }

  public findRequiredFilterWithoutValue(requiredConditionOperator: OperatorType): string[] {
    switch (requiredConditionOperator) {
    case 'or':
      if (this.filtersConfig.some(filterItem => filterItem.required && filterItem.value)) return [];
      return this.filtersConfig
        .filter(filterItem => filterItem.required)
        .map(filterItem => filterItem.requiredText ?? filterItem.placeholder);
    case 'and':
    default:
      return this.filtersConfig
        .filter(filterItem => filterItem.required && !filterItem.value)
        .map(filterItem => filterItem.requiredText ?? filterItem.placeholder)
      ;
    }
  }

  private setStatesForFilters(): void {
    this.filtersConfig.forEach((filterItem: IFilterConfig) => {
      let valueMapping = {};
      if (
        filterItem.type === FILTER_DATA_TYPE.MULTI_SELECT ||
        filterItem.type === FILTER_DATA_TYPE.NESTED_MULTI_SELECT ||
        filterItem.type === FILTER_DATA_TYPE.GROUP_MULTI_SELECT ||
        filterItem.type === FILTER_DATA_TYPE.GEOFENCE_MULTI_SELECT
      ) {
        valueMapping = {
          mapValueFromRouteParams: (params: Params, key: keyof Params) => {
            if (isIgnoredValue(filterItem, params[filterItem.key])) {
              return filterItem.defaultValue;
            } else {
              // This is a temporary fix, have to fix it in a generic way
              // TODO:
              // Added reportKey to check for expiration status filter only for driver qualification report
              // And vehicle document report, needs to be removed in the future
              if ((this.reportKey === 'driver-qualification-documents' || this.reportKey === 'vehicle-documents')
                && filterItem.key === 'expiration_status' && filterItem.type === FILTER_DATA_TYPE.MULTI_SELECT
              ) {
                return (mapArrayFromRouteParams(params, key) as string[])?.map((value: string) => parseInt(value, 10));
              }
              return mapArrayFromRouteParams(params, key);
            }
          },
          defaultValue: filterItem.defaultValue,
        };
        this.stateManager.set(filterItem.key, valueMapping);
      }

      if (filterItem.type === FILTER_DATA_TYPE.SINGLE_SELECT) {
        valueMapping = {
          mapValueFromRouteParams: (params: Params) => {
            if (isIgnoredValue(filterItem, params[filterItem.key])) return filterItem.defaultValue;
            else return params[filterItem.key];
          },
          defaultValue: filterItem.defaultValue,
        };
        this.stateManager.set(filterItem.key, valueMapping);
      }

      if (filterItem.type === FILTER_DATA_TYPE.TOGGLE_SELECT) {
        valueMapping = {
          mapValueFromRouteParams: (params: Params) => {
            if (params[filterItem.key] !== 'true' && params[filterItem.key] !== 'false') {
              return params[filterItem.key];
            }
            else return mapBooleanFromRouteParams(params, filterItem.key);
          },
          defaultValue: filterItem.defaultValue,
        };
        this.stateManager.set(filterItem.key, valueMapping);
      }

      if (filterItem.type === FILTER_DATA_TYPE.SEARCH) {
        valueMapping = {
          defaultValue: filterItem.defaultValue,
        };
        this.stateManager.set(filterItem.key, valueMapping);
      }

      if (filterItem.type === FILTER_DATA_TYPE.VIEW_AS) {
        valueMapping = {
          defaultValue: filterItem.defaultValue,
        };
        this.stateManager.set(filterItem.key, valueMapping);
      }

      if (filterItem.type === FILTER_DATA_TYPE.DATE_RANGE
        || filterItem.type === FILTER_DATA_TYPE.DATE_RANGE_PICKER_WITH_TYPE) {
        valueMapping = {
          // Change type to ISelectedRange after FF cleanup (AM-11522)
          setQueryValueInMap: (queryMap: Map<keyof Params, any>, range: any) => {
            if (range) {
              // Remove disabled tabs condition, when we have month selector in date range picker
              // It is only added because right now phx-date doesn't support month selector
              // will be using old selector when disabledTabs is present
              if (this.reportsPhxMigrationConfig.isDateRangeFilterPhxMigrationEnabled()
                && !filterItem.dateRangeOptions?.disabledTabs?.length) {
                queryMap.set(filterItem.key.start, format(range.start, PHX_DATE_FORMAT['yyyy-MM-dd']));
                queryMap.set(filterItem.key.end, format(range.end, PHX_DATE_FORMAT['yyyy-MM-dd']) );
              } else {
                queryMap.set(filterItem.key.start, range.start.format(DATE_FORMAT));
                queryMap.set(filterItem.key.end, range.end.format(DATE_FORMAT));
              }
            } else {
              queryMap.delete(filterItem.key.start);
              queryMap.delete(filterItem.key.end);
            }
          },
          mapValueFromRouteParams: (params: Params, keyState: keyof Params) => {
            // Removed disabled tabs condition, when we have month selector in date range picker
            if (this.reportsPhxMigrationConfig.isDateRangeFilterPhxMigrationEnabled()
              && !filterItem.dateRangeOptions?.disabledTabs?.length) {
              return mapSelectedDateRangeFromRouteParams(params, keyState, filterItem.key.start, filterItem.key.end);
            } else return mapDateRangeFromRouteParams(params, keyState, filterItem.key.start, filterItem.key.end);
          },
          defaultValue: filterItem?.dateRangeOptions?.defaultDateRangeValue,
        };
        this.stateManager.set(DATE_RANGE_KEY, valueMapping);
        // if date range has type we need to manage state accordingly
        if (filterItem.dateTypeConfig){
          this.stateManager.set(filterItem.dateTypeConfig.key, {
            mapValueFromRouteParams: (params: Params, keyState: keyof Params) => (params[keyState]),
            defaultValue: filterItem.dateTypeConfig.defaultValue,
          });
        }
      }

      if (filterItem.type === FILTER_DATA_TYPE.DATE_PICKER) {
        valueMapping = {
          setQueryValueInMap: (queryMap: Map<keyof Params, any>, range: any) => {
            if (range) {
              if (this.isDatePickersPhxMigrationEnabled) {
                queryMap.set(filterItem.key.start, format(range.start, PHX_DATE_FORMAT['yyyy-MM-dd']));
                queryMap.set(filterItem.key.end, format(range.start, PHX_DATE_FORMAT['yyyy-MM-dd']));
              } else {
                queryMap.set(filterItem.key.start, range.start.format(DATE_FORMAT));
                queryMap.set(filterItem.key.end, range.start.format(DATE_FORMAT));
              }
            } else {
              queryMap.delete(filterItem.key.start);
              queryMap.delete(filterItem.key.end);
            }
          },
          mapValueFromRouteParams: (params: Params, keyState: keyof Params) => {
            if (this.isDatePickersPhxMigrationEnabled) {
              return mapSelectedDateRangeFromRouteParams(params, keyState, filterItem.key.start, filterItem.key.start);
            } else return mapDateRangeFromRouteParams(params, keyState, filterItem.key.start, filterItem.key.start);
          },
          defaultValue: filterItem?.dateRangeOptions?.defaultDateRangeValue,
        };
        this.stateManager.set(DATE_RANGE_KEY, valueMapping);
      }

      if (filterItem.type === FILTER_DATA_TYPE.SORT) {
        valueMapping = {
          setQueryValueInMap: (queryMap: Map<keyof Params, any>, value: ISort) => {
            forIn(value, (val: string, type: keyof ISort) => queryMap.set(filterItem.key[type], val));
          },
          mapValueFromRouteParams: (params: Params) => {
            if (filterItem.possibleValues.includes(params[filterItem.key.field])) {
              return {
                field: params[filterItem.key.field],
                direction: params[filterItem.key.direction],
              };
            }
            return filterItem.defaultValue;
          },
          defaultValue: filterItem.defaultValue,
        };
        this.stateManager.set('sort', valueMapping);
      }

      if (filterItem.type === FILTER_DATA_TYPE.PAGINATION) {
        if (filterItem.key === PAGE_NO_KEY || filterItem.paginationType === 'key') {
          valueMapping = {
            defaultValue: filterItem.defaultValue,
            resetPage: false,
            isDistinct: false,
          };
          this.stateManager.set(filterItem.key, valueMapping);
        } else if (filterItem.key === PER_PAGE_KEY) {
          valueMapping = {
            defaultValue: filterItem.defaultValue,
          };
          this.stateManager.set(filterItem.key, valueMapping);
        } else {
          throw Error(`key for pagination must be ${PER_PAGE_KEY} or ${PAGE_NO_KEY}`);
        }
      }

      if (filterItem.type === FILTER_DATA_TYPE.VEHICLE_TYPES) {
        this.stateManager
          .set('years', {
            mapValueFromRouteParams: mapArrayFromRouteParams,
            defaultValue: null,
          })
          .set('classes', {
            mapValueFromRouteParams: mapArrayFromRouteParams,
            defaultValue: null,
          })
          .set('make_models', {
            defaultValue: null,
          })
          .set('fuel_types', {
            mapValueFromRouteParams: mapArrayFromRouteParams,
            defaultValue: null,
          });
      }

      if (filterItem.type === FILTER_DATA_TYPE.ENTITY_SELECT_FILTER) {
        this.stateManager
          .set(filterItem.key, {
            defaultValue: null,
          });
      }

      if (filterItem.type === FILTER_DATA_TYPE.ENTITY_TYPEAHEAD) {
        this.stateManager
        .set(filterItem.vehicleKey, {
          mapValueFromRouteParams: (params: Params) => params[filterItem.vehicleKey],
          defaultValue: null,
        })
        .set(filterItem.assetKey, {
          mapValueFromRouteParams: (params: Params) => params[filterItem.assetKey],
          defaultValue: null,
        })
        .set(filterItem.customAssetKey, {
          mapValueFromRouteParams: (params: Params) => params[filterItem.customAssetKey],
          defaultValue: null,
        })
        .set(filterItem.customVehicleKey, {
          mapValueFromRouteParams: (params: Params) => params[filterItem.customVehicleKey],
          defaultValue: null,
        })
        .set(filterItem.entityTypeKey, {
          mapValueFromRouteParams: (params: Params) => params[filterItem.entityTypeKey],
          defaultValue: null,
        }).set('vehicle_ids', {
          /**
           * All param keys should be set in the config, and should depend on the above three set methods.
           * The k2web endpoint for vehicle-inspection-detail report throws an error if asset_ids is type number
           * instead of number[], despite the fact that the value will always be singular.
           * We need to these vehicle_ids and asset_ids arrays until the k2web endpoint is updated.
           */
          mapValueFromRouteParams: mapArrayFromRouteParams,
          defaultValue: null,
        })
        .set('asset_ids', {
          mapValueFromRouteParams: mapArrayFromRouteParams,
          defaultValue: null,
        });
      }

      if (
        filterItem.type === FILTER_DATA_TYPE.DRIVER_TYPEAHEAD ||
        filterItem.type === FILTER_DATA_TYPE.VEHICLE_TYPEAHEAD ||
        filterItem.type === FILTER_DATA_TYPE.ASSET_TYPEAHEAD
      ) {
        this.stateManager.set(filterItem.key, {
          mapValueFromRouteParams: mapArrayFromRouteParams,
          defaultValue: null,
        });
      }

      if (
        filterItem.type === FILTER_DATA_TYPE.DRIVER_DISPATCHER_TYPEAHEAD
      ) {
        this.stateManager.set(filterItem.key, {
          mapValueFromRouteParams:  (params: Params) => params[filterItem.key],
          defaultValue: null,
        });
      }

      if (filterItem.type === FILTER_DATA_TYPE.VEHICLE_ASSET_SINGLE_SELECT) {
        this.stateManager
        .set(filterItem.vehicleKey, {
          mapValueFromRouteParams: (params: Params) => params[filterItem.vehicleKey],
          defaultValue: null,
        })
        .set(filterItem.assetKey, {
          mapValueFromRouteParams: (params: Params) => params[filterItem.assetKey],
          defaultValue: null,
        })
        .set(filterItem.entityTypeKey, {
          mapValueFromRouteParams: (params: Params) => params[filterItem.entityTypeKey],
          defaultValue: null,
        });
      }
    });
  }

  private initParamsStream(): void {
    this.stateManager.getQueryParamsStream()
      .pipe(
        filter(this.areParamsEqual.bind(this)),
        takeUntil(this.unsubscribe),
      )
      .subscribe((params: ReportParamsType) => {
        const paramsClone: ReportParamsType = cloneDeep(params);
        paramsClone.set(REPORT_ID_KEY, this.activatedRoute.snapshot.params[REPORT_ID_KEY]);
        paramsClone.set(REPORT_TYPE_KEY, this.activatedRoute.snapshot.params[REPORT_TYPE_KEY]);

        const paramsObj: Params = {};

        Array.from(paramsClone.keys()).forEach((paramKey: string) => {
          paramsObj[paramKey] = paramsClone.get(paramKey);
        });
        this.queryParamsSubject.next(paramsObj);
        this.queryParamsMapSubject.next(paramsClone);
      })
    ;
  }

  // checks if current params equal to the url params, and correct url params if they dont match
  private areParamsEqual(params: ReportParamsType): boolean {
    const urlParams = this.activatedRoute.snapshot.params;
    let areParamsEqual = true;
    let tempParam = cloneDeep(urlParams);
    Object.keys(urlParams).forEach((key: string) => {
      // multiselect filter value are different in url params, ignoring array and boolean values
      if (Array.isArray(params.get(key)) || params.get(key) === true || params.get(key) === false) return;
      if (params.get(key) && urlParams[key] !== params.get(key)) {
        tempParam = omit(tempParam, key);
        areParamsEqual = false;
      }
    });
    if (!areParamsEqual) this.changeUrlParamsWithReplace(tempParam);
    return areParamsEqual;
  }

  // set given params in url without adding it to the naviagation history
  private changeUrlParamsWithReplace(params: Params): void {
    const options = { replaceUrl: true, relativeTo: this.activatedRoute };
    this.router.navigate(['./', params], options);
  }
}
